import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DatePickerInput } from '../ui/DatePickerInput';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { 
  DollarSign, 
  Filter,
  Globe,
  Store,
  CreditCard,
  Banknote,
  Smartphone,
  Tag,
  ChevronDown,
  Check,
  Truck
} from 'lucide-react';
import { ReportsService } from '../../services/reports/reports.service';
import type { SalesSummary, SalesByDay, TopProduct, OrderDetail } from '../../services/reports/reports.service';
import { UsersService } from '../../services/users/users.service';
import { SuppliersService } from '../../services/suppliers/suppliers.service';
import { usePermissions } from '../../hooks/usePermissions';
import { getColombiaDate, toColombiaDateString } from '../../utils/date';
import { webSocketService, type NewOrderEvent, type OrderStatusChangeEvent } from '../../services/websocket/websocket.service';
import toast from 'react-hot-toast';

// Formatear moneda
const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

// Formatear fecha
const metodosPagoMap: Record<string, { label: string; icono: React.ReactNode; bg: string; color: string }> = {
  'EFECTIVO': { label: 'Efectivo', icono: <Banknote className="w-3.5 h-3.5" />, bg: 'bg-green-100', color: 'text-green-700' },
  'TARJETA': { label: 'Tarjeta', icono: <CreditCard className="w-3.5 h-3.5" />, bg: 'bg-purple-100', color: 'text-purple-700' },
  'CARD': { label: 'Tarjeta', icono: <CreditCard className="w-3.5 h-3.5" />, bg: 'bg-purple-100', color: 'text-purple-700' },
  'NEQUI': { label: 'Nequi', icono: <Smartphone className="w-3.5 h-3.5" />, bg: 'bg-blue-100', color: 'text-blue-700' },
  'DAVIPLATA': { label: 'Daviplata', icono: <Smartphone className="w-3.5 h-3.5" />, bg: 'bg-indigo-100', color: 'text-indigo-700' },
  'TRANSFERENCIA': { label: 'Transferencia', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'PSE': { label: 'PSE', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'BANCOLOMBIA_TRANSFER': { label: 'Bancolombia', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'EFECTY': { label: 'Efecty', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-orange-100', color: 'text-orange-700' },
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
  });
};

// Componente de tarjeta KPI
const KPICard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, subtitle, icon, iconBg, iconColor }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${iconBg}`}>
        <div className={iconColor}>{icon}</div>
      </div>
    </div>
  </div>
);

// Skeleton para KPIs
const KPICardSkeleton = () => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-32" />
      </div>
      <div className="p-3 rounded-xl bg-gray-200 w-12 h-12" />
    </div>
  </div>
);

// Componente de gráfico de barras apiladas
const StackedBarChart: React.FC<{ data: SalesByDay[] }> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.total), 1);
  
  return (
    <div className="h-48">
      <div className="flex items-end gap-2 h-40">
        {data.map((day) => {
          const onlineHeight = (day.online / maxValue) * 100;
          const posHeight = (day.pos / maxValue) * 100;
          
          return (
            <div key={day.fecha} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="w-full flex flex-col-reverse rounded-t-lg overflow-hidden bg-gray-100">
                {/* Barra POS */}
                <div 
                  className="w-full bg-purple-500 transition-all duration-300 group-hover:bg-purple-600"
                  style={{ height: `${posHeight}%`, minHeight: posHeight > 0 ? '2px' : '0' }}
                  title={`POS: ${formatMoney(day.pos)}`}
                />
                {/* Barra Online */}
                <div 
                  className="w-full bg-blue-500 transition-all duration-300 group-hover:bg-blue-600"
                  style={{ height: `${onlineHeight}%`, minHeight: onlineHeight > 0 ? '2px' : '0' }}
                  title={`Online: ${formatMoney(day.online)}`}
                />
              </div>
              {/* Tooltip con total */}
              <div className="opacity-0 group-hover:opacity-100 absolute -mt-8 bg-gray-900 text-white text-xs px-2 py-1 rounded transition-opacity pointer-events-none">
                {formatMoney(day.total)}
              </div>
            </div>
          );
        })}
      </div>
      {/* Labels */}
      <div className="flex justify-between mt-2 px-1">
        {data.map((day) => (
          <span key={day.fecha} className="text-[10px] text-gray-400 flex-1 text-center">
            {formatDate(day.fecha)}
          </span>
        ))}
      </div>
    </div>
  );
};

export const ReportesVentasContent: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = usePermissions();
  
  // Estados de filtros
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const [tipoFiltro, setTipoFiltro] = useState<'ALL' | 'ONLINE' | 'POS'>('ALL');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [vendedorId, setVendedorId] = useState<string>('');
  const [vendedores, setVendedores] = useState<Array<{ id: string; nombreCompleto: string }>>([]);
  const [proveedorId, setProveedorId] = useState<string>('');

  // Calcular fechas según el rango seleccionado (en zona horaria Colombia)
  const getDates = () => {
    const hoy = getColombiaDate();
    const hoyStr = toColombiaDateString(hoy);
    let inicioStr = hoyStr;

    switch (dateRange) {
      case 'today':
        inicioStr = hoyStr;
        break;
      case 'week': {
        const inicio = new Date(hoy);
        inicio.setDate(hoy.getDate() - 7);
        inicioStr = toColombiaDateString(inicio);
        break;
      }
      case 'month': {
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        inicioStr = toColombiaDateString(inicio);
        break;
      }
      case 'custom':
        return {
          inicio: fechaInicio || hoyStr,
          fin: fechaFin || hoyStr,
        };
    }

    return { inicio: inicioStr, fin: hoyStr };
  };

  // Construir filtros para las queries
  const filters = useMemo(() => {
    const { inicio, fin } = getDates();

    return {
      fechaInicio: inicio,
      fechaFin: fin,
      tipo: tipoFiltro,
      vendedorId: vendedorId || undefined,
      proveedorId: proveedorId || undefined,
    };
  }, [dateRange, tipoFiltro, vendedorId, proveedorId, fechaInicio, fechaFin]);

  // Queries independientes con React Query
  const summaryQuery = useQuery({
    queryKey: ['sales-summary', filters, proveedorId],
    queryFn: () => ReportsService.getSalesSummary(filters),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const byDayQuery = useQuery({
    queryKey: ['sales-by-day', filters, proveedorId],
    queryFn: () => ReportsService.getSalesByDay(filters),
    staleTime: 5 * 60 * 1000,
  });

  const topProductsQuery = useQuery({
    queryKey: ['top-products', filters, proveedorId],
    queryFn: () => ReportsService.getTopProducts(filters, 5),
    staleTime: 5 * 60 * 1000,
  });

  const ordersQuery = useQuery({
    queryKey: ['orders-detail', filters, proveedorId],
    queryFn: () => ReportsService.getOrdersDetail(filters, 1, 10),
    staleTime: 5 * 60 * 1000,
  });

  const summary = summaryQuery.data?.success ? summaryQuery.data.data : null;
  const salesByDay = byDayQuery.data?.success ? byDayQuery.data.data : [];
  const topProducts = topProductsQuery.data?.success ? topProductsQuery.data.data : [];
  const orders = ordersQuery.data?.success ? ordersQuery.data.data.data : [];

  const isLoading = summaryQuery.isLoading || byDayQuery.isLoading || topProductsQuery.isLoading || ordersQuery.isLoading;
  const isInitialLoading = summaryQuery.isFetching && !summaryQuery.data;

  // Cargar vendedores (solo una vez)
  useEffect(() => {
    const loadVendedores = async () => {
      try {
        const response = await UsersService.getUsers(1, 100);
        if (response.data) {
          setVendedores(response.data.map((u: any) => ({ id: u.id, nombreCompleto: u.nombreCompleto })));
        }
      } catch {
        // Silencioso
      }
    };
    loadVendedores();
  }, []);

  // Cargar proveedores
  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-dropdown-reportes'],
    queryFn: async () => {
      const r = await SuppliersService.getAllForSelect();
      return r.success ? r.data : [];
    },
    staleTime: Infinity,
  });

  // Escuchar eventos de WebSocket para actualizar reportes en tiempo real
  useEffect(() => {
    // Asegurar que el WebSocket esté conectado
    const token = localStorage.getItem('accessToken');
    if (token && webSocketService.getConnectionStatus() === 'disconnected') {
      webSocketService.connect(token).catch(() => {});
    }

    const handleNewOrder = (event: NewOrderEvent) => {
      invalidateReportQueries();
    };

    const handleOrderStatusChange = (event: OrderStatusChangeEvent) => {
      invalidateReportQueries();
    };

    const invalidateReportQueries = () => {
      queryClient.invalidateQueries({ queryKey: ['sales-summary'] });
      queryClient.invalidateQueries({ queryKey: ['sales-by-day'] });
      queryClient.invalidateQueries({ queryKey: ['top-products'] });
      queryClient.invalidateQueries({ queryKey: ['orders-detail'] });
      queryClient.invalidateQueries({ queryKey: ['sold-products'] });
    };

    webSocketService.addEventListener('new_order', handleNewOrder);
    webSocketService.addEventListener('order_status_change', handleOrderStatusChange);

    return () => {
      webSocketService.removeEventListener('new_order', handleNewOrder);
      webSocketService.removeEventListener('order_status_change', handleOrderStatusChange);
    };
  }, [queryClient]);

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {/* Filtro de fechas */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {[
            { key: 'today', label: 'Hoy' },
            { key: 'week', label: 'Semana' },
            { key: 'month', label: 'Mes' },
            ...(isSuperAdmin() ? [{ key: 'custom', label: 'Personalizado' }] : []),
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setDateRange(opt.key as any)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                dateRange === opt.key
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filtro de tipo */}
        <Listbox value={tipoFiltro} onChange={setTipoFiltro}>
          <div className="relative">
            <ListboxButton className="relative w-[180px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {tipoFiltro === 'ALL' ? 'Todas las ventas' : tipoFiltro === 'ONLINE' ? 'Online' : 'Punto de Venta'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              {[
                { value: 'ALL', label: 'Todas las ventas' },
                { value: 'ONLINE', label: 'Online' },
                { value: 'POS', label: 'Punto de Venta' },
              ].map((opt) => (
                <ListboxOption
                  key={opt.value}
                  value={opt.value}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{opt.label}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Filtro de vendedor */}
        {/* Filtro de vendedor */}
        <Listbox value={vendedorId} onChange={setVendedorId}>
          <div className="relative">
            <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {vendedorId ? vendedores.find((v) => v.id === vendedorId)?.nombreCompleto : 'Todos los vendedores'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <ListboxOption
                value=""
                className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
              >
                <span className="block truncate">Todos los vendedores</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                  <Check className="h-4 w-4" />
                </span>
              </ListboxOption>
              {vendedores.map((v) => (
                <ListboxOption
                  key={v.id}
                  value={v.id}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{v.nombreCompleto}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Filtro de proveedor */}
        <Listbox value={proveedorId} onChange={setProveedorId}>
          <div className="relative">
            <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {proveedorId ? proveedores?.find((p) => p.id === proveedorId)?.nombre : 'Todos los proveedores'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <ListboxOption
                value=""
                className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
              >
                <span className="block truncate">Todos los proveedores</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                  <Check className="h-4 w-4" />
                </span>
              </ListboxOption>
              {proveedores?.map((p) => (
                <ListboxOption
                  key={p.id}
                  value={p.id}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{p.nombre}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {dateRange === 'custom' && isSuperAdmin() && (
          <div className="flex items-center gap-2">
            <DatePickerInput
              value={fechaInicio}
              onChange={setFechaInicio}
              placeholder="Fecha inicio"
            />
            <span className="text-gray-500">a</span>
            <DatePickerInput
              value={fechaFin}
              onChange={setFechaFin}
              placeholder="Fecha fin"
            />
          </div>
        )}
        <button
          onClick={() => {
            summaryQuery.refetch();
            byDayQuery.refetch();
            topProductsQuery.refetch();
            ordersQuery.refetch();
          }}
          className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          title="Recargar datos"
        >
          <Filter className="w-5 h-5" />
        </button>

        {proveedorId && (
          <button
            onClick={() => setProveedorId('')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Truck className="w-4 h-4 text-emerald-600" />
            <span className="truncate max-w-[150px]">
              {proveedores?.find((p) => p.id === proveedorId)?.nombre}
            </span>
            <span className="text-gray-400">×</span>
          </button>
        )}
      </div>

      {/* KPIs */}
      {isInitialLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard
            title="Total Ventas"
            value={formatMoney(summary.totales.totalGeneral)}
            subtitle={`${summary.totales.totalTransacciones} transacciones`}
            icon={<DollarSign className="w-6 h-6" />}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
          />
          <KPICard
            title="Ventas Online"
            value={formatMoney(summary.totales.ventasOnline)}
            subtitle={`${summary.totales.cantidadOnline} pedidos`}
            icon={<Globe className="w-6 h-6" />}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />
          <KPICard
            title="Ventas POS"
            value={formatMoney(summary.totales.ventasPOS)}
            subtitle={`${summary.totales.cantidadPOS} ventas`}
            icon={<Store className="w-6 h-6" />}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
          />
          <KPICard
            title="Ticket Promedio"
            value={formatMoney(summary.promedios.ticketPromedioGeneral)}
            subtitle="Por transacción"
            icon={<CreditCard className="w-6 h-6" />}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
        </div>
      ) : null}

      {/* Gráfico y Top Productos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Gráfico de ventas */}
        <div className="lg:col-span-2 p-5 rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Ventas por Día</h3>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-sm" />
                <span className="text-gray-500">Online</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-sm" />
                <span className="text-gray-500">POS</span>
              </div>
            </div>
          </div>
          {byDayQuery.isLoading && !byDayQuery.data ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            </div>
          ) : salesByDay.length > 0 ? (
            <StackedBarChart data={salesByDay} />
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              No hay datos para mostrar
            </div>
          )}
        </div>

        {/* Top Productos */}
        <div className="p-5 rounded-xl border border-gray-200 bg-white">
          <h3 className="font-bold mb-4 text-gray-900">Productos Más Vendidos</h3>
          {topProductsQuery.isLoading && !topProductsQuery.data ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-lg bg-gray-200" />
                  <div className="flex-1 h-4 bg-gray-200 rounded" />
                  <div className="w-16 h-4 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-gray-200 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate text-gray-900">{product.nombre}</p>
                    <p className="text-xs text-gray-500">{product.cantidadVendida} unidades</p>
                  </div>
                  <p className="font-bold text-emerald-600 text-sm">
                    {formatMoney(product.totalVentas)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabla de ventas recientes */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Ventas Recientes</h3>
          <button
            onClick={() => navigate('/dashboard/ventas')}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Ver todas
          </button>
        </div>
        {ordersQuery.isLoading && !ordersQuery.data ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pedido</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-sm text-gray-900">{order.numeroPedido}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        order.tipo === 'ONLINE' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {order.tipo === 'ONLINE' ? <Globe className="w-3 h-3" /> : <Store className="w-3 h-3" />}
                        {order.tipo}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-900">{order.cliente}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{order.vendedor || '—'}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{new Date(order.fecha).toLocaleDateString('es-CO')}</td>
                    <td className="px-5 py-3">
                      {(() => {
                        const mp = metodosPagoMap[order.metodoPago] || { label: order.metodoPago || '—', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-gray-100', color: 'text-gray-700' };
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${mp.bg} ${mp.color}`}>
                            {mp.icono}
                            {mp.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-emerald-600">{formatMoney(order.total)}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        order.estado === 'DELIVERED' || order.estado === 'PAID'
                          ? 'bg-green-100 text-green-700'
                          : order.estado === 'PENDING_PAYMENT'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {order.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
